import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createStandardCourse, getStandardCourseAhaSummaries, listStandardCourses, retireStandardCourse, startStandardCourseVersion,
} from "../../../api/backend";
import { Cat, ConfirmDialog, FilterChip, Modal, Notice, PageHeader, Panel, StatePanel, StatusChip } from "../ui/AdminUi";
import { I } from "../ui/icons";
import CatalogTab from "./CatalogTab";
import StandardCourseEditor from "./StandardCourseEditor";
import { errorText } from "./common";
import { summaryChip } from "./integrations/model";
import "./integrations/integrations.css";

const EMPTY = { title: "", courseNumber: "", category: "AHA" };
const SHOW = [["Active", "Active"], ["Retired", "Retired"], ["All", "All"]];
const NEW_COURSE_CATEGORIES = ["AHA", "Firefighter", "EMS", "Rescue", "Hazmat", "General"];

/**
 * Standardize courses: courses taught identically at every training center. A course is built once here, published as
 * a version that never changes, and centers pull it from the Knowledge Bank. Retiring asks for the course number.
 */
export default function StandardCoursesView({ initialView = "courses" }) {
  const [courses, setCourses] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [view, setView] = useState(initialView === "catalog" ? "catalog" : "courses");
  const [editingTab, setEditingTab] = useState("details");
  const [addProductSignal, setAddProductSignal] = useState(0);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [show, setShow] = useState("Active");
  const [category, setCategory] = useState("all");
  const [retiring, setRetiring] = useState(null);

  const load = useCallback(() => listStandardCourses()
    .then((data) => { setCourses(Array.isArray(data) ? data : []); setError(null); })
    .catch((e) => { setCourses([]); setError(errorText(e, "The standard courses could not be loaded.")); }), []);

  // The Integrations column is an extra: if the summaries cannot be loaded the list still works, with the column blank.
  const loadSummaries = useCallback(() => getStandardCourseAhaSummaries().then((m) => setSummaries(m || {})).catch(() => setSummaries({})), []);

  useEffect(() => { load(); loadSummaries(); }, [load, loadSummaries]);

  const openCourse = (id, tab = "details") => { setEditingTab(tab); setEditing(id); };

  const categories = useMemo(() => [...new Set((courses || []).map((c) => c.category).filter(Boolean))], [courses]);
  const rows = (courses || []).filter((c) => (show === "All" || (show === "Retired" ? c.status === "RETIRED" : c.status !== "RETIRED"))
    && (category === "all" || c.category === category));

  const create = async () => {
    setSaving(true);
    setCreateError(null);
    try {
      const created = await createStandardCourse(form);
      setCreating(false);
      setForm(EMPTY);
      await load();
      openCourse(created.id);
    } catch (e) {
      setCreateError(errorText(e, "The course could not be created."));
    } finally {
      setSaving(false);
    }
  };

  const act = async (action, fallback, message) => {
    setError(null);
    setNotice(null);
    try {
      await action();
      if (message) setNotice(message);
      await load();
    } catch (e) {
      setError(errorText(e, fallback));
    }
  };

  if (editing) {
    return (
      <StandardCourseEditor courseId={editing} initialTab={editingTab}
        onBack={() => { setEditing(null); load(); loadSummaries(); }}
        onPublished={() => { setEditing(null); setNotice("Published. Training centers that offer this category can now use it."); load(); loadSummaries(); }} />
    );
  }

  return (
    <div data-testid="standard-courses-view">
      <PageHeader
        title="Standardize courses"
        desc="Industry-standard courses built once for every training center. A published version never changes; a change becomes a new version."
        actions={view === "catalog" ? (
          <>
            <button type="button" className="b sec" onClick={() => { setCreateError(null); setCreating(true); }}><I.plus s={14} />New standard course</button>
            <button type="button" className="b pri" onClick={() => setAddProductSignal((n) => n + 1)}>Add product</button>
          </>
        ) : (
          <>
            <button type="button" className="b sec" onClick={() => setView("catalog")}>Manage products</button>
            <button type="button" className="b pri" onClick={() => { setCreateError(null); setCreating(true); }}><I.plus s={14} />New standard course</button>
          </>
        )}
      />

      <div className="seg sc-top" role="tablist" aria-label="View">
        <button type="button" role="tab" aria-selected={view === "courses"} className={view === "courses" ? "on" : ""} onClick={() => setView("courses")}>Courses{courses && <span className="c" style={{ marginLeft: 6, opacity: 0.7 }}>{courses.filter((c) => c.status !== "RETIRED").length}</span>}</button>
        <button type="button" role="tab" aria-selected={view === "catalog"} className={view === "catalog" ? "on" : ""} onClick={() => setView("catalog")}>Catalog</button>
      </div>

      {notice && <div style={{ marginBottom: 16 }}><Notice kind="ok">{notice} <button type="button" className="b ghost sm" onClick={() => setNotice(null)}>Dismiss</button></Notice></div>}
      {error && courses?.length > 0 && <div style={{ marginBottom: 16 }}><Notice kind="bad">{error} <button type="button" className="b ghost sm" onClick={() => setError(null)}>Dismiss</button></Notice></div>}

      {view === "catalog" && <CatalogTab addProductSignal={addProductSignal} onOpenCourse={(id, tab) => { setView("courses"); openCourse(id, tab); }} />}

      {view === "courses" && <Panel>
        <div className="panel-h">
          <div className="seg" role="group" aria-label="Show courses">
            {SHOW.map(([key, label]) => (
              <button key={key} type="button" className={show === key ? "on" : ""} aria-pressed={show === key} onClick={() => setShow(key)}>{label}</button>
            ))}
          </div>
          <span className="sp" />
          <span className="sub" style={{ fontSize: 12.5, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}><StatusChip status="Published" label="Live" />what centers pull</span>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}><StatusChip status="Draft" />only you can see</span>
          </span>
        </div>
        {courses?.length > 0 && (
          <div className="panel-h" style={{ gap: 8 }}>
            <span className="eyebrow" style={{ marginRight: 4 }}>Category</span>
            {categories.length > 6 ? (
              <select className="sel" style={{ width: 220, height: 32 }} aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="all">All categories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <>
                <FilterChip label="All categories" on={category === "all"} onClick={() => setCategory("all")} />
                {categories.map((c) => <FilterChip key={c} label={c} on={category === c} onClick={() => setCategory(c)} />)}
              </>
            )}
          </div>
        )}

        {courses === null ? (
          <StatePanel kind="loading" what="standard courses" />
        ) : error && courses.length === 0 ? (
          <StatePanel kind="error" what="standard courses" error={error} onRetry={load} />
        ) : courses.length === 0 ? (
          <StatePanel
            kind="empty"
            what="standard courses"
            why="Standard courses are built here once and pulled by every training center. Start with the AHA course your centers all teach."
            action={<button type="button" className="b pri" onClick={() => setCreating(true)}><I.plus s={14} />New standard course</button>}
          />
        ) : rows.length === 0 ? (
          <StatePanel kind="empty" what={`${show.toLowerCase()} courses`} why="No courses match these filters. Change them to see others." />
        ) : (
          <div className="tw">
            <table className="tbl">
              <thead>
                <tr><th>Course</th><th>Category</th><th>Lifecycle</th><th>Integrations</th><th className="r">Classes using it</th><th className="r">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const retired = c.status === "RETIRED";
                  return (
                    <tr key={c.id} data-testid={`standard-row-${c.id}`} className={!retired && c.hasDraft ? "click" : undefined} style={{ opacity: retired ? 0.7 : 1 }}
                      onClick={() => !retired && c.hasDraft && openCourse(c.id)}>
                      <td>
                        <b style={{ fontWeight: 600 }}>{c.title}</b>
                        {c.courseNumber && <div style={{ marginTop: 3 }}><span className="code">{c.courseNumber}</span></div>}
                      </td>
                      <td><Cat c={c.category} /></td>
                      <td><Lifecycle course={c} /></td>
                      <td data-testid={`integrations-${c.id}`} onClick={(e) => { e.stopPropagation(); openCourse(c.id, "integrations"); }}><IntegrationsChip course={c} summary={summaries[c.id]} /></td>
                      <td className="r mono">{c.classCount ?? 0}</td>
                      <td className="r" onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          {!retired && c.hasDraft && <button type="button" className="b sec sm" onClick={() => openCourse(c.id)}><I.pencil s={12} />Edit draft</button>}
                          {!retired && !c.hasDraft && (
                            <button type="button" className="b sec sm"
                              onClick={() => act(async () => { await startStandardCourseVersion(c.id); openCourse(c.id); }, "A new version could not be started.")}>
                              <I.plus s={12} />Start new version
                            </button>
                          )}
                          {!retired && !c.hasDraft && <button type="button" className="b ghost sm" onClick={() => openCourse(c.id, "integrations")}><I.link s={12} />Integrations</button>}
                          {!retired && <button type="button" className="b dsec sm" onClick={() => setRetiring(c)}>Retire</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>}

      <NewCourseDialog open={creating} form={form} setForm={setForm} saving={saving} createError={createError} onCancel={() => setCreating(false)} onCreate={create} />

      <ConfirmDialog
        open={Boolean(retiring)}
        title={retiring ? `Retire ${retiring.title}?` : "Retire?"}
        confirmLabel="Retire course"
        typed={retiring ? (retiring.courseNumber || retiring.title) : undefined}
        consequence={retiring ? "Training centers can no longer pull this course into their Knowledge Bank or create new classes from it." : ""}
        keeps={retiring ? `The ${retiring.classCount ?? 0} classes already created keep running on the version they were created from.` : ""}
        onClose={() => setRetiring(null)}
        onConfirm={() => {
          const course = retiring;
          setRetiring(null);
          act(() => retireStandardCourse(course.id), "The course could not be retired.", `${course.title} is retired. Classes already created keep running.`);
        }}
      />
    </div>
  );
}

/** The worst-case AHA state of one course in the environment this backend is connected to. */
function IntegrationsChip({ course, summary }) {
  const chip = summaryChip(summary, course);
  if (!chip) return <span className="sub" style={{ fontSize: 13 }}>None</span>;
  const Icon = I[chip.icon] || I.minus;
  return <span className={`chip ${chip.tone}`}><Icon s={12} />{chip.text}</span>;
}

/** Lifecycle of one course: the live version, a draft in progress, or retired. Always the same chips. */
function Lifecycle({ course }) {
  if (course.status === "RETIRED") return <StatusChip status="Retired" label={`Retired${course.currentVersionNumber ? ` · v${course.currentVersionNumber}` : ""}`} />;
  return (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
      {course.currentVersionNumber
        ? <StatusChip status="Published" label={`Live · v${course.currentVersionNumber}`} />
        : <StatusChip status="Unknown" label="Not published" />}
      {course.hasDraft && <StatusChip status="Draft" label={course.currentVersionNumber ? "New version in progress" : "Draft"} />}
    </span>
  );
}

function NewCourseDialog({ open, form, setForm, saving, createError, onCancel, onCreate }) {
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  return (
    <Modal
      open={open}
      title="New standard course"
      labelId="new-standard-title"
      onClose={() => !saving && onCancel()}
      footer={(
        <>
          <button type="button" className="b ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="b pri" onClick={onCreate} disabled={saving || !form.title.trim() || !form.category.trim()}>
            {saving ? "Creating…" : "Create and start building"}
          </button>
        </>
      )}
    >
      <p className="cons sub" style={{ fontSize: 13.5 }}>Creates draft version 1. It belongs to no training center and is not visible to centers until you publish.</p>
      <label className="field">Title<span className="inp"><input value={form.title} onChange={set("title")} required autoFocus placeholder="e.g. AHA PALS Provider" /></span></label>
      <div className="g2">
        <label className="field">Course number<span className="inp"><input className="mono" value={form.courseNumber} onChange={set("courseNumber")} placeholder="AHA-PALS" /></span></label>
        <label className="field">Category
          <select className="sel" value={form.category} onChange={set("category")}>
            {NEW_COURSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="h">Fixed once published.</span>
        </label>
      </div>
      {createError && <Notice kind="bad">{createError}</Notice>}
    </Modal>
  );
}
